/**
 * Checkbox: a native input under finixt-ui's drawn box.
 *
 * finixt-ui's is a span with an onClick, which the keyboard cannot reach.
 * The input here is real and invisible, the same size as the box, so Space,
 * the label, `name`, `id`, and `aria-*` all work and the form sees a value;
 * the box draws from the input's state. Checked is a near-black fill, not
 * orange: ticking a box is an ordinary action. Without a `label` the wrapper
 * is a span, so an outer label of the caller's own can hold the row.
 */
import { useEffect, useRef, type CSSProperties, type InputHTMLAttributes } from 'react';

import { Icon } from './finixt.js';

const HIDDEN_INPUT: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  margin: 0,
  opacity: 0,
  cursor: 'inherit',
};

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'type' | 'style'
> {
  checked?: boolean | undefined;
  /** Draws a dash instead of a tick, for a partially selected parent. */
  indeterminate?: boolean | undefined;
  /** Receives the next value, not an event. */
  onChange?: ((next: boolean) => void) | undefined;
  label?: string | undefined;
  /** Second line under the label. */
  hint?: string | undefined;
  style?: CSSProperties | undefined;
}

export function Checkbox({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  hint,
  disabled,
  style,
  ...rest
}: CheckboxProps) {
  const input = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);
  const on = checked || indeterminate;
  const Wrapper = label ? 'label' : 'span';
  return (
    <Wrapper
      className="ui-check"
      style={{
        display: 'inline-flex',
        alignItems: hint ? 'flex-start' : 'center',
        gap: 9,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        ...style,
      }}
    >
      <span
        data-box
        style={{
          position: 'relative',
          width: 17,
          height: 17,
          flexShrink: 0,
          marginTop: hint ? 2 : 0,
          borderRadius: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: on ? 'var(--fill-action)' : 'var(--surface-card)',
          border: `1px solid ${on ? 'var(--fill-action)' : 'var(--border-input)'}`,
          transition: 'background var(--dur-micro) var(--ease-standard)',
        }}
      >
        <input
          ref={input}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange?.(event.target.checked)}
          style={HIDDEN_INPUT}
          {...rest}
        />
        {on ? (
          <Icon name={indeterminate ? 'remove' : 'check'} size={13} color="var(--text-inverse)" />
        ) : null}
      </span>
      {label ? (
        <span style={{ minWidth: 0 }}>
          <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-body)' }}>
            {label}
          </span>
          {hint ? (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--text-meta)',
                color: 'var(--text-faint)',
                marginTop: 2,
              }}
            >
              {hint}
            </span>
          ) : null}
        </span>
      ) : null}
    </Wrapper>
  );
}
